import { generatePath, matchPath } from 'react-router';

import { all, call, put, select, take, takeEvery } from 'redux-saga/effects';
import { validate } from 'uuid';

import { PROJECTION } from 'constants/ApiConstants';
import { PATH } from 'constants/RoutesConstants';
import { scopeToPage } from 'modules/app/sagas/utils';
import { cleanChecklistsSelect } from 'modules/checklists/actions/dashboard';
import { fetchGroup, initializedGroupTab, selectGroup, setGroupConfigReady } from 'modules/groups/actions';
import { getSelectedGroup, getSelectedGroupId } from 'modules/groups/selectors';
import { LOCATION_CHANGE } from 'modules/router/actionTypes';
import { navigate } from 'modules/router/navigate';
import { getLocation } from 'modules/router/selectors';

import * as actionTypes from '../actionTypes';
import { DEFAULT_TAB } from '../constants';

/**  Preloads group data with config projection. */
function* preloadGroupConfig(groupId) {
  const selectedGroupId = yield select(getSelectedGroupId);
  const action = selectedGroupId === groupId ? fetchGroup : selectGroup;

  yield put(action(groupId, { projection: PROJECTION.CONFIG }, { forceReload: true }));
}

/**  Redirects to a valid group when the current groupId is invalid. */
function* redirectToValidGroup(tabKey = DEFAULT_TAB) {
  const group = yield select(getSelectedGroup);

  if (group?.id) {
    const redirectTo = generatePath(PATH.GROUP_CONFIG_PAGE.path, {
      groupId: group.id,
      tabKey,
    });
    yield call(navigate, redirectTo);
  }
}

/** Waits for group selection to complete. */
function* selectGroupWaiter() {
  const { error } = yield take([actionTypes.FETCH_GROUP_SUCCESS, actionTypes.FETCH_GROUP_FAILURE]);
  return Boolean(error);
}

/** Extracts the current active tab key from the URL. */
function* getCurrentActiveTabKey() {
  const location = yield select(getLocation);
  const match = matchPath(PATH.GROUP_CONFIG_PAGE.path, location.pathname);
  return match?.params?.tabKey ?? DEFAULT_TAB;
}

/**
 * Initializes a group configuration tab with lazy loading.
 *
 * This is the core function of the lazy loading architecture. It's called whenever:
 * - A user navigates to a new tab (LOCATION_CHANGE action)
 * - The group configuration page is first loaded
 *
 * **Flow:**
 * 1. Extracts tab key from URL (e.g., 'afterSales', 'parts', 'sales', 'selfService')
 * 2. Checks if tab has already been initialized (via visitedTabsSet)
 * 3. If not visited, looks up tab's initializer in tableFnDictionary
 * 4. Calls the initializer to load tab-specific data
 * 5. Marks tab as visited to prevent redundant loads
 */
function* initializeGroupTab(visitedTabsSet) {
  const tabKey = yield call(getCurrentActiveTabKey);

  if (!visitedTabsSet.has(tabKey)) {
    /** TODO to be check if initializer is needed for now I have commented each initializer
    *  If yes then revert the deletion of the tableFnDictionary file in the commit of this message
    * */
    // const hasInitializer = typeof tableFnDictionary[tabKey] === 'function';
    // if (hasInitializer) {
    //   yield call(tableFnDictionary[tabKey], visitedTabsSet);
    // }
    visitedTabsSet.add(tabKey);
  }
  yield put(initializedGroupTab(true));
}

/**
 * Initializes the group configuration page.
 * @param {Set<string>} visitedTabsSet - Set tracking visited tabs
 */
function* initializeGroupConfigPage(visitedTabsSet) {
  const location = yield select(getLocation);
  const match = matchPath(PATH.GROUP_CONFIG_PAGE.path, location.pathname);
  const { groupId, tabKey = DEFAULT_TAB } = match?.params ?? {};

  if (validate(groupId)) {
    yield call(preloadGroupConfig, groupId);

    const error = yield call(selectGroupWaiter);
    if (!error) {
      yield put(cleanChecklistsSelect());
      yield call(initializeGroupTab, visitedTabsSet);
      yield put(setGroupConfigReady(true));
    }
  } else {
    yield call(redirectToValidGroup, tabKey);
  }
}

/**
 * Handles redirection when a different group is selected.
 * @param {Object} action - The SELECT_GROUP action
 * @param {Object} action.payload - Action payload
 * @param {string} action.payload.groupId - The new group ID
 */
function* redirectToGroupPage({ payload: { groupId } }) {
  yield all([put(setGroupConfigReady(false)), call(selectGroupWaiter)]);
  const location = yield select(getLocation);
  const match = matchPath(PATH.GROUP_CONFIG_PAGE.path, location.pathname);
  const tabKey = match?.params?.tabKey ?? DEFAULT_TAB;
  const redirectTo = generatePath(PATH.GROUP_CONFIG_PAGE.path, { groupId, tabKey });
  yield call(navigate, redirectTo);
}

/** Main worker for the group configuration page. */
function* onGroupConfigWorker() {
  const visitedTabsSet = new Set();
  yield call(initializeGroupConfigPage, visitedTabsSet);
  yield takeEvery(actionTypes.SELECT_GROUP, redirectToGroupPage);
  yield takeEvery(LOCATION_CHANGE, initializeGroupTab, visitedTabsSet);
}

const channelPattern = ({ type: actionType, payload }) =>
  actionType === LOCATION_CHANGE && payload.action !== 'REPLACE';

function* onLeaveGroupConfig() {
  yield put(setGroupConfigReady(false));
}

/** Root saga for group configuration. */
export default function* groupConfigSaga() {
  yield scopeToPage([PATH.GROUP_CONFIG_PAGE], onGroupConfigWorker, onLeaveGroupConfig, channelPattern);
}
